import { PAIR_PLUS_PAYTABLE, type ThreecardRules } from '@tv-casino/shared';
import styles from './ThreeCardBet.module.css';

/** Chip step for Pair Plus: small, because it is a long shot. */
const PAIR_STEP = 5;

interface ThreeCardBetProps {
  rules: ThreecardRules;
  /** Chips free to stake, before anything staged here. */
  chips: number;
  /** The staged Pair Plus stake. */
  pairPlus: Partial<Record<'pair_plus', number>>;
  onPairPlus: (next: Partial<Record<'pair_plus', number>>) => void;
  busy: boolean;
}

const PAY_LINES: { key: keyof typeof PAIR_PLUS_PAYTABLE; label: string }[] = [
  { key: 'straight_flush', label: 'Straight flush' },
  { key: 'trips', label: 'Three of a kind' },
  { key: 'straight', label: 'Straight' },
  { key: 'flush', label: 'Flush' },
  { key: 'pair', label: 'Pair' },
];

/**
 * The Pair Plus stepper. The ante itself rides the shared chip stepper beside
 * it; this is the one side wager the game carries, so it gets its own card —
 * paytable printed, because nobody was born knowing 30:1.
 */
export function ThreeCardBet({ rules, chips, pairPlus, onPairPlus, busy }: ThreeCardBetProps) {
  const amount = pairPlus.pair_plus ?? 0;

  const set = (next: number) => {
    if (next <= 0) onPairPlus({});
    else onPairPlus({ pair_plus: next });
  };

  return (
    <section className={styles.wrap}>
      {rules.pairPlus ? (
        <>
          <p className={styles.heading}>Pair Plus</p>
          <div className={styles.stepper}>
            <button
              type="button"
              className={styles.step}
              disabled={busy || amount === 0}
              onClick={() => set(amount - PAIR_STEP)}
              aria-label="Lower the Pair Plus bet"
            >
              −
            </button>
            <span className={amount > 0 ? styles.amountOn : styles.amount}>{amount}</span>
            <button
              type="button"
              className={styles.step}
              disabled={
                busy || amount + PAIR_STEP > rules.pairPlusMax || amount + PAIR_STEP > chips
              }
              onClick={() => set(amount + PAIR_STEP)}
              aria-label="Raise the Pair Plus bet"
            >
              +
            </button>
          </div>
          <ul className={styles.pays}>
            {PAY_LINES.map(({ key, label }) => (
              <li key={key}>
                <span>{label}</span>
                <span>{PAIR_PLUS_PAYTABLE[key]}:1</span>
              </li>
            ))}
          </ul>
          <p className={styles.hint}>
            Pair Plus settles whatever your three cards make — fold and it still counts.
          </p>
        </>
      ) : (
        <p className={styles.hint}>Pair Plus is switched off at this table.</p>
      )}
    </section>
  );
}
